// Angular
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Translate
import { TranslateService } from '@ngx-translate/core';
// Store
import { Store } from '@ngrx/store';
import { AppState } from '../../../../core/reducers';
// Auth
import { AuthNoticeService, AuthService, Login } from '../../../../core/auth';
import { LayoutUtilsService } from '../../../../core/_base/crud';

@Component({
	selector: 'kt-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {
	// Public params
	loginForm: FormGroup;
	loading = false;
	isLoggedIn$: Observable<boolean>;
	errors: any = [];

	private unsubscribe: Subject<any>;

	private returnUrl: any;

	// Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param router: Router
	 * @param auth: AuthService
	 * @param authNoticeService: AuthNoticeService
	 * @param translate: TranslateService
	 * @param store: Store<AppState>
	 * @param fb: FormBuilder
	 * @param cdr
	 * @param route
	 */
	constructor(
		private router: Router,
		private auth: AuthService,
		private authNoticeService: AuthNoticeService,
		private translate: TranslateService,
		private layoutUtilsService: LayoutUtilsService,
		private store: Store<AppState>,
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
		private route: ActivatedRoute
	) {
		this.unsubscribe = new Subject();
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.initLoginForm();

		// redirect back to the returnUrl before login
		this.route.queryParams.subscribe(params => {
			this.returnUrl = params['returnUrl'] || '/';
		});
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.authNoticeService.setNotice(null);
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.loading = false;
	}

	/**
	 * Form initalization
	 * Default params, validators
	 */
	initLoginForm() {

		this.loginForm = this.fb.group({
			login_by: [1],
			mobile: [null, Validators.compose([
				Validators.minLength(7),
				Validators.maxLength(16) 
			])
			],
			employee_number: ['', Validators.compose([
				Validators.required,
				Validators.minLength(4),
				Validators.maxLength(10)
			])
			],
			password: ['', Validators.compose([
				Validators.required,
				Validators.minLength(3),
				Validators.maxLength(100)
			])
			]
		});

		this.loginForm.controls.login_by.valueChanges.subscribe((val) => {
           if(val === 1){
             this.loginForm.controls.mobile.setValue(null);
             this.loginForm.controls.mobile.clearValidators();
             this.loginForm.controls.mobile.updateValueAndValidity({emitEvent:false});
             this.loginForm.controls.employee_number.setValidators([Validators.required,Validators.minLength(4),Validators.maxLength(10)]);
             this.loginForm.controls.employee_number.updateValueAndValidity({emitEvent:false});
		   }else if(val === 2){
			this.loginForm.controls.employee_number.setValue(null);
			this.loginForm.controls.employee_number.clearValidators();
			this.loginForm.controls.employee_number.updateValueAndValidity({emitEvent:false});
			this.loginForm.controls.mobile.setValidators([Validators.required,Validators.minLength(7),Validators.maxLength(16) ]);
			this.loginForm.controls.mobile.updateValueAndValidity({emitEvent:false});
		   }
		})
	}

	/**
	 * Form Submit
	 */
	submit() {
		const controls = this.loginForm.controls;
		/** check form */
		if (this.loginForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			return;
		}

		this.loading = true;

		const authData = {
			mobile: controls['mobile'].value,
			login_by: controls['login_by'].value,
			employee_number: controls['employee_number'].value,
			password: controls['password'].value
		};

		this.auth
			.login(authData.mobile,authData.employee_number, authData.password)
			.pipe(
				tap(auth => {
					if (auth) {
						this.store.dispatch(new Login({authToken: auth.access_token,role_id:auth.user.role_id,user_id:auth.user.user_id}));
						this.router.navigateByUrl(this.returnUrl); // Main page
						if(auth.message && auth.message.length > 0){
							this.layoutUtilsService.infoMessage(auth.message);
						}
					} else {
						this.authNoticeService.setNotice(this.translate.instant('AUTH.VALIDATION.INVALID_LOGIN'), 'danger');
					}
				}),
				takeUntil(this.unsubscribe),
				finalize(() => {
					this.loading = false;
					this.cdr.markForCheck();
				})
			)
			.subscribe((res) => {

			},(error:any) => {
				if(error && error.error){
					this.authNoticeService.setNotice(error.error.message,'danger')
				}
			});
	}

	/**
	 * Checking control validation
	 *
	 * @param controlName: string => Equals to formControlName
	 * @param validationType: string => Equals to valitors name
	 */
	isControlHasError(controlName: string, validationType: string): boolean {
		const control = this.loginForm.controls[controlName];
		if (!control) {
			return false;
		}

		const result = control.hasError(validationType) && (control.dirty || control.touched);
		return result;
	}

	textDirection(){
		return this.translate.currentLang == 'en' ? 'text-left' : 'text-right';
	}
}
